import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Rutas } from '@/constants/rutas';
import CustomLink from '@/components/shared/custom/custom-link';
import FantasyStatsSection from './FantasyStatsSection';
import EstadisticasEnVivoTabs from './EstadisticasEnVivoTabs';
import NoDataComponent from '@/components/shared/no-data-component';
import { FANTASY } from '@/helpers/biwenger-helper';
import { biwengerScores } from '@/helpers/biwenger-helper';
import { getTimeShortWithMinutesLongWithYear } from '@/helpers/datetime-helper';
import { translate } from '@/data/fixture-transalations';
import { apiFootballLeagues } from '@/data/ligas';

const EstadisticasEnVivo = ({ estadisticas, fixture, fixtureId, isLaLiga }) => {
    // Procesar estadísticas por modo - ahora cada modo tiene home y away directamente
    const processedStats = useMemo(() => {
        if (!estadisticas || !estadisticas.estadisticas || estadisticas.estadisticas.length === 0) {
            return [];
        }

        return estadisticas.estadisticas.map((modo) => ({
            mode: modo.mode,
            subMode: modo.subMode,
            home: modo.home || null,
            away: modo.away || null,
        }));
    }, [estadisticas]);

    const leagueName = useMemo(() => {
        return apiFootballLeagues.find((l) => l.id == fixture?.leagueId)?.name || 'La Liga';
    }, [fixture?.leagueId]);

    // Generar tabs y secciones
    const tabs = useMemo(() => {
        const tabsList = [];
        const sections = [];

        // Helper para mapear jugadores de la respuesta
        const mapPlayers = (team) => {
            if (!team || !team.players || !Array.isArray(team.players)) {
                return [];
            }
            return team.players.map((player) => ({
                name: player.name,
                score: player.score,
                id: 0, // No tenemos ID de jugador en la nueva estructura
            }));
        };

        // La Liga Fantasy
        const fantasyStats = processedStats.find((s) => s.mode === FANTASY.FANTASY && !s.subMode);
        if (fantasyStats) {
            const homePlayers = mapPlayers(fantasyStats.home);
            const awayPlayers = mapPlayers(fantasyStats.away);
            if (homePlayers.length > 0 || awayPlayers.length > 0) {
                tabsList.push({
                    id: 'la-liga-fantasy',
                    label: 'La Liga Fantasy',
                    mode: FANTASY.FANTASY,
                });
                sections.push(
                    <FantasyStatsSection
                        key="la-liga-fantasy"
                        title="La Liga Fantasy"
                        homePlayers={homePlayers}
                        awayPlayers={awayPlayers}
                        mode={FANTASY.FANTASY}
                        fixture={fixture}
                        sectionId="la-liga-fantasy"
                    />
                );
            }
        }

        // Analitica Fantasy
        const analiticaStats = processedStats.find((s) => s.mode === FANTASY.ANALITICA);
        if (analiticaStats) {
            const homePlayers = mapPlayers(analiticaStats.home);
            const awayPlayers = mapPlayers(analiticaStats.away);
            if (homePlayers.length > 0 || awayPlayers.length > 0) {
                tabsList.push({
                    id: 'analitica-fantasy',
                    label: 'Analítica Fantasy',
                    mode: FANTASY.ANALITICA,
                });
                sections.push(
                    <FantasyStatsSection
                        key="analitica-fantasy"
                        title="Analítica Fantasy"
                        homePlayers={homePlayers}
                        awayPlayers={awayPlayers}
                        mode={FANTASY.ANALITICA}
                        fixture={fixture}
                        sectionId="analitica-fantasy"
                    />
                );
            }
        }

        // Fantasy Marca
        const fantasyMarcaStats = processedStats.find((s) => s.mode === FANTASY.FANTASY_MARCA);
        if (fantasyMarcaStats) {
            const homePlayers = mapPlayers(fantasyMarcaStats.home);
            const awayPlayers = mapPlayers(fantasyMarcaStats.away);
            if (homePlayers.length > 0 || awayPlayers.length > 0) {
                tabsList.push({
                    id: 'fantasy-marca',
                    label: 'Fantasy Marca',
                    mode: FANTASY.FANTASY_MARCA,
                });
                sections.push(
                    <FantasyStatsSection
                        key="fantasy-marca"
                        title="Fantasy Marca"
                        homePlayers={homePlayers}
                        awayPlayers={awayPlayers}
                        mode={FANTASY.FANTASY_MARCA}
                        fixture={fixture}
                        sectionId="fantasy-marca"
                    />
                );
            }
        }

        // Mister Fantasy
        const misterFantasyStats = processedStats.find((s) => s.mode === FANTASY.MISTER_FANTASY);
        if (misterFantasyStats) {
            const homePlayers = mapPlayers(misterFantasyStats.home);
            const awayPlayers = mapPlayers(misterFantasyStats.away);
            if (homePlayers.length > 0 || awayPlayers.length > 0) {
                tabsList.push({
                    id: 'mister-fantasy',
                    label: 'Mister Fantasy',
                    mode: FANTASY.MISTER_FANTASY,
                });
                sections.push(
                    <FantasyStatsSection
                        key="mister-fantasy"
                        title="Mister Fantasy"
                        homePlayers={homePlayers}
                        awayPlayers={awayPlayers}
                        mode={FANTASY.MISTER_FANTASY}
                        fixture={fixture}
                        sectionId="mister-fantasy"
                    />
                );
            }
        }

        // Biwenger submodes
        const biwengerStats = processedStats.filter((s) => s.mode === FANTASY.BIWENGER);
        biwengerStats.forEach((biwengerStat) => {
            const submode = biwengerScores.find((bs) => bs.id === biwengerStat.subMode);
            if (submode) {
                const homePlayers = mapPlayers(biwengerStat.home);
                const awayPlayers = mapPlayers(biwengerStat.away);
                if (homePlayers.length > 0 || awayPlayers.length > 0) {
                    const id = `biwenger-${submode.kind.toLowerCase().replace(/\s+/g, '-')}`;
                    tabsList.push({
                        id,
                        label: submode.name,
                        mode: FANTASY.BIWENGER,
                    });
                    sections.push(
                        <FantasyStatsSection
                            key={id}
                            title={submode.name}
                            homePlayers={homePlayers}
                            awayPlayers={awayPlayers}
                            mode={FANTASY.BIWENGER}
                            subMode={biwengerStat.subMode}
                            fixture={fixture}
                            sectionId={id}
                        />
                    );
                }
            }
        });

        return { tabs: tabsList, sections };
    }, [processedStats]);

    if (!estadisticas || !estadisticas.estadisticas || estadisticas.estadisticas.length === 0) {
        return (
            <>
                <Typography variant="h1">Estadísticas fantasy</Typography>
                <NoDataComponent
                    title="Las estadísticas se actualizarán pronto"
                    description="Los datos estarán disponibles cuando comience el partido y se actualicen las puntuaciones."
                    severity="info"
                />
                <Typography variant="body2" mt={2}>
                    Puedes consultar los onces posibles, rachas, y otras estadísticas fantasy de este partido
                    en la sección de{' '}
                    <CustomLink
                        href={`${Rutas.PARTIDO}/${fixtureId}/alineaciones-probables`}
                        className="link"
                    >
                        alineaciones probables
                    </CustomLink>
                </Typography>
            </>
        );
    }

    if (tabs.tabs.length === 0) {
        return (
            <>
                <Typography variant="h1">Estadísticas fantasy</Typography>
                <NoDataComponent
                    title="No hay estadísticas disponibles"
                    description="Las puntuaciones se actualizarán pronto cuando haya datos disponibles."
                    severity="info"
                />
            </>
        );
    }

    return (
        <>
            <EstadisticasEnVivoTabs tabs={tabs.tabs} />

            {tabs.sections}

            <Typography variant="body2" mb={1} fontSize={14}>
                El partido entre {fixture?.home?.name} y {fixture?.away?.name} correspondiente a {leagueName},{' '}
                {translate(fixture?.round)} se disputó el {getTimeShortWithMinutesLongWithYear(fixture?.date)}
                .
            </Typography>
            <Typography variant="body2" fontSize={14}>
                El encuentro terminó con un resultado de {fixture?.home?.goals} a {fixture?.away?.goals}. Se
                muestran las estadísticas de los modos de fantasy disponibles en nuestra web.
            </Typography>
        </>
    );
};

export default EstadisticasEnVivo;
